import dayjs, { Dayjs } from "dayjs";

/**
 * The stretch of time the clinic dashboard is looking at.
 *
 * A calendar year rather than an arbitrary range, because that is how the numbers are
 * read: month against month, and this year against last. It also lines up with what the
 * API does with the period it is given -- it answers with the twelve months ending in the
 * end date's month, so a whole year in yields January to December out.
 */

export interface ResolvedPeriod {
    /** ISO dates, inclusive at both ends -- what the API takes. */
    from: string;
    to: string;
}

/** Enough to cover the clinic's whole history, and to keep covering it for years. */
export const YEARS_OFFERED = 5;

/**
 * The years on offer, newest first -- an admin nearly always wants this one or the last.
 *
 * `today` is a parameter rather than read from the clock so the turn of the year is
 * testable rather than something that happens once and is noticed in January.
 *
 * The list is fixed rather than derived from the data. Asking the server which years it
 * holds would need its own query and either a second round trip or a field you cannot
 * request without already having chosen a year -- a lot of machinery to stop somebody
 * selecting a year the clinic did not exist in, which already renders as the empty state.
 */
export const yearOptions = (today: Dayjs = dayjs()): number[] =>
    Array.from({ length: YEARS_OFFERED }, (_unused, index) => today.year() - index);

/**
 * A calendar year as the pair of dates the API takes.
 *
 * The end is 31 December even for the year in progress, deliberately: the server derives
 * its twelve trend months as the end date's month and the eleven before it, so an earlier
 * end would hand back a window finishing mid-year. Nothing in this system is dated in the
 * future, so no total changes -- except the deferred figure, which is "as at period end"
 * and therefore means the end of December. Its help text says so.
 *
 * Written as string literals rather than through `dayjs().endOf('year')`, which is a
 * timezone-sensitive way to express a constant.
 */
export const periodForYear = (year: number): ResolvedPeriod => ({
    from: `${year}-01-01`,
    to: `${year}-12-31`,
});
